import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { MaterialModule } from '../../../shared/material.module';
import { CountryFlat } from '../../../models/country';
import { Helpers } from '../../../utilities/helpers';
import { LutService } from '../../../services/lut.service';
import { CorporateGovernanceService } from '../../../services/corporate-governance.service';
import { CorporateGovernanceProblemType } from '../../../models/enums/corporateGovernanceProblemType.enum';
import { CorporateGovernanceResultDto } from '../../../models/api/corporateGovernanceResultDto';
import { CorporateGovernanceProblemTypeDto } from '../../../models/api/corporateGovernanceProblemTypeDto';

const PROBLEM_TYPE_LABELS: Record<string, string> = {
  IndependentOverTwoPercent: 'Independent over 2%',
  TwoCeosOrMore: 'Two CEOs or more',
  TwoChairmenOrMore: 'Two chairmen or more',
  AgeOver100OrUnder18: 'Age over 100 or under 18',
  ShareholderOver100Percent: 'Shareholder over 100%',
  FreeFloatUnderOrEqual5: 'Free float ≤ 5%',
  MeetingAttendanceAverageOver100: 'Meeting attendance average over 100%',
  IndependentOverTenure: 'Independent over tenure',
  IndependentFormerExecutive: 'Independent former executive',
  FindFinancialExpert: 'Find financial expert',
  FindPoliticalConnection: 'Find political connection'
};

@Component({
  selector: 'app-check-corporate-governance',
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    NgxSpinnerModule,
    RouterLink
  ],
  templateUrl: './check-corporate-governance.component.html',
  styleUrl: './check-corporate-governance.component.scss'
})
export class CheckCorporateGovernanceComponent implements OnInit {
  private fb = inject(FormBuilder);
  private lutService = inject(LutService);
  private corporateGovernanceService = inject(CorporateGovernanceService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);

  ProblemType = CorporateGovernanceProblemType;

  /** Loaded from the API, which also says which checks support review. */
  problemTypes: CorporateGovernanceProblemTypeDto[] = [];
  problemSubTypes: string[] = [];
  countries: CountryFlat[] = [];
  measurements = Helpers.measurements();

  results: CorporateGovernanceResultDto[] = [];
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];
  sortBy = 'Company';
  ascending = true;
  hasSearched = false;
  lastSearchedProblemType: CorporateGovernanceProblemType | null = null;

  /** Review keys whose save is in flight, so their checkboxes disable until it lands. */
  reviewingRows = new Set<string>();

  /**
   * Columns depend on the check that produced the rows: only the holding checks carry the
   * three share-class percentages, only the tenure check carries a tenure. Driven by the
   * problem type the results actually came from, not the one currently picked in the form.
   */
  get displayedColumns(): string[] {
    switch (this.lastSearchedProblemType) {
      // A director's recorded birth year is wrong wherever they sit, so the check reports the
      // person once and has no company, country or measurement to put against them.
      case CorporateGovernanceProblemType.AgeOver100OrUnder18:
        return ['name', 'age'];

      // Company-level findings: no one director is at fault.
      case CorporateGovernanceProblemType.ShareholderOver100Percent:
      case CorporateGovernanceProblemType.FreeFloatUnderOrEqual5:
        return ['company', 'country', 'measurement',
                'percentage', 'secondClassPercentage', 'thirdClassPercentage'];
      case CorporateGovernanceProblemType.TwoCeosOrMore:
      case CorporateGovernanceProblemType.TwoChairmenOrMore:
        return ['company', 'country', 'measurement'];
      case CorporateGovernanceProblemType.MeetingAttendanceAverageOver100:
        return ['company', 'country', 'measurement', 'percentage'];

      case CorporateGovernanceProblemType.IndependentOverTwoPercent:
        return ['name', 'company', 'country', 'measurement',
                'percentage', 'secondClassPercentage', 'thirdClassPercentage'];
      case CorporateGovernanceProblemType.IndependentOverTenure:
        return ['name', 'company', 'country', 'measurement', 'tenure', 'reviewed'];

      default:
        return ['name', 'company', 'country', 'measurement'];
    }
  }

  /**
   * Header of a share-class percentage column. The same three columns carry a holding for the
   * 2% check, the identified total for Shareholder over 100% and what is left over for Free
   * float, so the header has to say which.
   */
  classPercentageHeader(shareClass: '1st' | '2nd' | '3rd'): string {
    switch (this.lastSearchedProblemType) {
      case CorporateGovernanceProblemType.ShareholderOver100Percent:
        return `Held ${shareClass} %`;
      case CorporateGovernanceProblemType.FreeFloatUnderOrEqual5:
        return `Free Float ${shareClass} %`;
      case CorporateGovernanceProblemType.MeetingAttendanceAverageOver100:
        return 'Average Attendance %';
      default:
        return `${shareClass} Class %`;
    }
  }

  filtersForm = this.fb.group({
    problemType: [null as CorporateGovernanceProblemType | null, Validators.required],
    problemSubType: [''],
    countryId: [''],
    year: [''],
    hideReviewed: [false]
  });

  ngOnInit(): void {
    this.lutService.getVisibleCountries().subscribe({
      next: (countries) => this.countries = countries.sort((a, b) => a.name.localeCompare(b.name)),
      error: () => this.toast.error('Error loading countries')
    });

    this.corporateGovernanceService.getProblemTypes().subscribe({
      next: (types) => this.problemTypes = types,
      error: () => this.toast.error('Error loading problem types')
    });

    this.filtersForm.controls.problemType.valueChanges.subscribe((problemType) => {
      this.filtersForm.controls.problemSubType.setValue('');
      this.problemSubTypes = [];

      if (problemType === CorporateGovernanceProblemType.FindFinancialExpert) {
        this.lutService.getFinancialExpertise().subscribe({
          next: (items) => this.problemSubTypes = items.map(i => i.name).sort((a, b) => a.localeCompare(b)),
          error: () => this.toast.error('Error loading financial expertise')
        });
      } else if (problemType === CorporateGovernanceProblemType.FindPoliticalConnection) {
        this.lutService.getPoliticalPositions().subscribe({
          next: (items) => this.problemSubTypes = items.map(i => i.name).sort((a, b) => a.localeCompare(b)),
          error: () => this.toast.error('Error loading political positions')
        });
      }
    });
  }

  requiresSubType(problemType: CorporateGovernanceProblemType | null): boolean {
    return problemType === CorporateGovernanceProblemType.FindFinancialExpert
      || problemType === CorporateGovernanceProblemType.FindPoliticalConnection;
  }

  supportsReview(problemType: CorporateGovernanceProblemType | null): boolean {
    if (problemType === null || problemType === undefined) return false;
    return this.problemTypes.find(t => t.value === problemType)?.supportsReview ?? false;
  }

  /** A review belongs to the director at the company, whatever measurement the row is from. */
  reviewKey(row: CorporateGovernanceResultDto): string {
    return `${row.directorId}|${row.companyId}`;
  }

  /**
   * Confirms a director is correct at a company, or takes that back. The mark covers every
   * measurement, so every row of that director at that company on the page follows the one
   * ticked, in place. When the list hides reviewed rows the page is reloaded instead, as in
   * Check Family Status: the server drops every measurement of the director, including rows on
   * later pages, so removing only the ones in view would leave the total and the paging wrong.
   */
  onReviewedChange(row: CorporateGovernanceResultDto, reviewed: boolean): void {
    const key = this.reviewKey(row);
    const problemType = this.lastSearchedProblemType;
    if (!row.directorId || !row.companyId || problemType === null || this.reviewingRows.has(key)) return;

    this.reviewingRows.add(key);
    this.corporateGovernanceService.setReviewed(problemType, row.directorId, row.companyId, reviewed).subscribe({
      next: () => {
        this.reviewingRows.delete(key);
        this.results.filter(r => this.reviewKey(r) === key).forEach(r => r.reviewed = reviewed);

        if (reviewed && this.filtersForm.controls.hideReviewed.value) {
          this.loadResults();
        }
      },
      error: () => {
        this.reviewingRows.delete(key);
        this.toast.error('Error saving the review');
      }
    });
  }

  getProblemTypeLabel(name: string): string {
    return PROBLEM_TYPE_LABELS[name] ?? name;
  }

  getCompanyQueryParams(year?: number): Record<string, string | number> {
    const params: Record<string, string | number> = {};
    const tab = this.getCompanyTab();
    if (tab) params['tab'] = tab;
    if (year !== undefined && year !== null) params['year'] = year;
    return params;
  }

  private getCompanyTab(): string | null {
    switch (this.lastSearchedProblemType) {
      case CorporateGovernanceProblemType.IndependentOverTwoPercent:
      case CorporateGovernanceProblemType.TwoCeosOrMore:
      case CorporateGovernanceProblemType.TwoChairmenOrMore:
      case CorporateGovernanceProblemType.MeetingAttendanceAverageOver100:
      case CorporateGovernanceProblemType.IndependentOverTenure:
      case CorporateGovernanceProblemType.IndependentFormerExecutive:
        return 'board';
      case CorporateGovernanceProblemType.ShareholderOver100Percent:
      case CorporateGovernanceProblemType.FreeFloatUnderOrEqual5:
        return 'ownership';
      default:
        return null;
    }
  }

  onSearch(): void {
    if (this.filtersForm.invalid) {
      this.filtersForm.markAllAsTouched();
      return;
    }

    const form = this.filtersForm.value;
    if (this.requiresSubType(form.problemType ?? null) && !form.problemSubType) {
      this.toast.error('Sub-type is required for this problem type');
      return;
    }

    this.currentPage = 1;
    this.loadResults();
  }

  onSortChange(sort: Sort): void {
    if (sort.direction) {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
      this.loadResults();
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadResults();
  }

  onExport(): void {
    if (this.filtersForm.invalid) {
      this.filtersForm.markAllAsTouched();
      return;
    }

    const form = this.filtersForm.value;
    if (this.requiresSubType(form.problemType ?? null) && !form.problemSubType) {
      this.toast.error('Sub-type is required for this problem type');
      return;
    }
    if (form.problemType === null || form.problemType === undefined) {
      return;
    }

    this.spinnerService.show();
    this.corporateGovernanceService.exportCsv({
      problemType: form.problemType,
      problemSubType: form.problemSubType || undefined,
      countryId: form.countryId || undefined,
      year: form.year ? +form.year : undefined,
      hideReviewed: this.supportsReview(form.problemType) && (form.hideReviewed ?? false)
    }).subscribe({
      next: (response) => {
        this.downloadCsv(response.body!, this.getFilename(response, `corporate_governance_${CorporateGovernanceProblemType[form.problemType!]}`));
        this.spinnerService.hide();
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Error exporting CSV');
      }
    });
  }

  onExportAll(): void {
    const form = this.filtersForm.value;

    this.spinnerService.show();
    this.corporateGovernanceService.exportAllCsv(
      form.countryId || undefined,
      form.year ? +form.year : undefined
    ).subscribe({
      next: (response) => {
        this.downloadCsv(response.body!, this.getFilename(response, 'corporate_governance_all'));
        this.spinnerService.hide();
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Error exporting all CSV');
      }
    });
  }

  private getFilename(response: { headers: { get(name: string): string | null } }, fallbackPrefix: string): string {
    const disposition = response.headers.get('Content-Disposition');
    if (disposition) {
      const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
      if (match?.[1]) {
        return decodeURIComponent(match[1]);
      }
    }
    return `${fallbackPrefix}_${new Date().toISOString().split('T')[0]}.csv`;
  }

  private downloadCsv(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private loadResults(): void {
    const form = this.filtersForm.value;
    if (form.problemType === null || form.problemType === undefined) {
      return;
    }

    this.spinnerService.show();
    this.corporateGovernanceService.search({
      problemType: form.problemType,
      problemSubType: form.problemSubType || undefined,
      countryId: form.countryId || undefined,
      year: form.year ? +form.year : undefined,
      hideReviewed: this.supportsReview(form.problemType) && (form.hideReviewed ?? false),
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending
    }).subscribe({
      next: (response) => {
        // Rows dropping out under "Hide reviewed" can leave the current page past the last one.
        const lastPage = Math.max(1, Math.ceil(response.totalCount / this.pageSize));
        if (this.currentPage > lastPage) {
          this.currentPage = lastPage;
          this.loadResults();
          return;
        }

        this.results = response.data;
        this.totalItems = response.totalCount;
        this.hasSearched = true;
        this.lastSearchedProblemType = form.problemType ?? null;
        this.spinnerService.hide();
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Error running corporate governance check');
      }
    });
  }
}
